import i18n from 'i18next'
import * as Yup from 'yup'
import { DATE_FORMAT, DATE_SEPARATOR } from '../../../config'
import {
  PartnerType,
  LegalFormType,
  ValidTypeEntered,
} from '../../../models/enums'
import { checkTls } from '../../../services/api/checkTls'
import { hasEmployeeDiscount } from '../../../services/common/utils'

interface TlsDeps {
  onTlsEvaluated: (result: boolean) => void
  getLastCheckedEmail?: () => string | null
  setLastCheckedEmail?: (email: string | null) => void
  getLastTls?: () => { email: string | null; result: boolean | null }
  setLastTls?: (email: string | null, result: boolean | null) => void
}

export const regExText =
  /^[^\s][\sa-zA-ZÄÅáâäàãåÉéêëèíîïìÖóôöòõÜúüûùÇçÑñŠšß.'-]*$/
export const regExTextZipCode = /^[^\s][0-9a-zA-Z-]*$/
export const regExTextStreet =
  /^[^\s][0-9\sa-zA-ZÄÅáâäàãåÉéêëèíîïìÖóôöòõÜúüûùÇçÑñŠšß.'-ß|/()`´'-._]*$/
export const regExEmail =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,61}))$/
export const regExNumber = /^[0-9]*$/
export const regExTextPlace =
  /^[^\s|(|)][\sa-zA-ZÄÅáâäàãåÉéêëèíîïìÖóôöòõÜúüûùÇçÑñŠšß.'\-()]*$/
export const regExTextStreetNumber = /^[^\s][0-9\sa-zA-ZäöüÄÖÜ'-ß|/()`´'-._]*$/
export const regexDkxNumber = /^[0-9a-zA-Z]*$/

export const VALIDATION_ERRORS = {
  inputRequired: (fieldValue: string) =>
    `Bitte füllen Sie das Pflichtfeld ${fieldValue} aus.`,
  valueInvalid: (typeEntered: ValidTypeEntered) =>
    `Bitte geben Sie nur ${typeEntered} an.`,
  valueInvalidGeneric: () => 'Bitte geben Sie das richtige Format ein',
  valueRequired: (fieldValue: string) =>
    `Bitte füllen Sie das Pflichtfeld für ${fieldValue} aus.`,
}

const customerDataShape = (isVtiGis: boolean | undefined, tlsDeps: TlsDeps) => {
  return {
    firstName: Yup.string()
      .matches(
        regExText,
        VALIDATION_ERRORS.valueInvalid(ValidTypeEntered.LETTERS)
      )
      .min(2, i18n.t('validation:minLength', { value: 2 }))
      .max(35, i18n.t('validation:maxLength', { value: 35 }))
      .required(
        VALIDATION_ERRORS.inputRequired(i18n.t('personal-details:firstName'))
      ),
    lastName: Yup.string()
      .matches(
        regExText,
        VALIDATION_ERRORS.valueInvalid(ValidTypeEntered.LETTERS)
      )
      .min(2, i18n.t('validation:minLength', { value: 2 }))
      .max(35, i18n.t('validation:maxLength', { value: 35 }))
      .required(
        VALIDATION_ERRORS.inputRequired(i18n.t('personal-details:lastName'))
      ),
    address: Yup.object().shape({
      street: Yup.string()
        .required(
          VALIDATION_ERRORS.inputRequired(i18n.t('personal-details:street'))
        )
        .matches(regExTextStreet, i18n.t('validation:invalidCharacter'))
        .min(2, i18n.t('validation:minLength', { value: 2 }))
        .max(60, i18n.t('validation:maxLength', { value: 60 })),
      houseNumber: Yup.string()
        .required(
          VALIDATION_ERRORS.inputRequired(
            i18n.t('personal-details:houseNumber')
          )
        )
        .matches(regExTextStreetNumber, i18n.t('validation:invalidCharacter'))
        .min(1, i18n.t('validation:minLength', { value: 1 }))
        .max(10, i18n.t('validation:maxLength', { value: 10 })),
      zipCode: Yup.string()
        .required(
          VALIDATION_ERRORS.inputRequired(i18n.t('personal-details:zipCode'))
        )
        .matches(regExTextZipCode, i18n.t('validation:invalidCharacter'))
        .min(3, i18n.t('validation:minLength', { value: 3 }))
        .max(10, i18n.t('validation:maxLength', { value: 10 })),
      city: Yup.string()
        .required(
          VALIDATION_ERRORS.inputRequired(i18n.t('personal-details:city'))
        )
        .matches(
          regExTextPlace,
          VALIDATION_ERRORS.valueInvalid(ValidTypeEntered.LETTERS)
        )
        .min(2, i18n.t('validation:minLength', { value: 2 }))
        .max(35, i18n.t('validation:maxLength', { value: 35 })),
      countryCode: Yup.string().test(
        'my-test-name',
        VALIDATION_ERRORS.inputRequired(i18n.t('personal-details:countryCode')),
        (value) => value !== 'NONE'
      ),
    }),

    contactData: Yup.object().shape({
      email: Yup.string()
        .matches(regExEmail, i18n.t('validation:invalidEmail'))
        .required(
          VALIDATION_ERRORS.inputRequired(i18n.t('personal-details:email'))
        )
        .test(
          'tls-check',
          i18n.t('validation:invalidEmailTlsCheck'),
          async function (value) {
            if (!value || !regExEmail.test(value)) return true

            const lastTls = tlsDeps.getLastTls?.()
            if (lastTls && lastTls.email === value) {
              if (lastTls.result === false) return false
              if (lastTls.result === true) return true
              // if lastTls.result === null the validation continues
            }
            if (tlsDeps.getLastCheckedEmail?.() === value) return true
            tlsDeps.setLastCheckedEmail?.(value)

            try {
              const result = await checkTls(value)
              tlsDeps.onTlsEvaluated(result)
              tlsDeps.setLastTls?.(value, result)
              return result
            } catch {
              tlsDeps.onTlsEvaluated(false)
              tlsDeps.setLastTls?.(value, false)
              tlsDeps.setLastCheckedEmail?.(null)
              return false
            }
          }
        ),
      mobilePhoneCountryCode: Yup.string().required(
        VALIDATION_ERRORS.inputRequired(
          i18n.t('personal-details:mobilePhoneCountryCode')
        )
      ),
    }),
  }
}

export const ValidationSchemaBusiness = Yup.object().shape({
  companyData: Yup.object().shape({
    entityLegalForm: Yup.string()
      .required(
        VALIDATION_ERRORS.inputRequired(
          i18n.t('personal-details:entityLegalForm.title')
        )
      )
      .oneOf(
        [
          LegalFormType.SOLE_TRADER,
          LegalFormType.REGISTERED_SOLE_TRADER,
          LegalFormType.FREELANCER,
        ],
        VALIDATION_ERRORS.inputRequired(
          i18n.t('personal-details:entityLegalForm.title')
        )
      ),
    name: Yup.string()
      .matches(
        regExText,
        VALIDATION_ERRORS.valueInvalid(ValidTypeEntered.LETTERS)
      )
      .min(2, i18n.t('validation:minLength', { value: 2 }))
      .max(35, i18n.t('validation:maxLength', { value: 35 }))
      .required(
        VALIDATION_ERRORS.inputRequired(i18n.t('personal-details:companyName'))
      ),
  }),
})
export const ValidationSchemaPrivate = (tlsDeps: TlsDeps) =>
  Yup.object().shape({
    customerData: Yup.object().shape({
      ...customerDataShape(false, tlsDeps),
      dateOfBirth: Yup.string()
        .formatOfDate(
          DATE_FORMAT,
          DATE_SEPARATOR,
          i18n.t('validation:invalidDateformat')
        )
        .notInFuture(DATE_SEPARATOR, i18n.t('validation:dateInFuture'))
        .fullAge(18, DATE_SEPARATOR, i18n.t('validation:fullAge'))
        .required(
          VALIDATION_ERRORS.inputRequired(
            i18n.t('personal-details:dateOfBirth')
          )
        ),
    }),
  })

export const ValidationSchemaVtiGis = (partnerType: PartnerType, tlsDeps: TlsDeps) =>
  Yup.object().shape({
    ...(hasEmployeeDiscount() && {
      dkxNumber: Yup.string()
        .matches(
          regexDkxNumber,
          VALIDATION_ERRORS.valueInvalidGeneric()
        )
        .required(VALIDATION_ERRORS.inputRequired(
          i18n.t('personal-details:dkxNumber')
        ))
    }),
    customerData: Yup.object().shape({
      ...customerDataShape(true, tlsDeps),
      dateOfBirth: Yup.string()
        .formatOfDate(
          DATE_FORMAT,
          DATE_SEPARATOR,
          i18n.t('validation:invalidDateformat')
        )
        .notInFuture(DATE_SEPARATOR, i18n.t('validation:dateInFuture'))
        .fullAge(18, DATE_SEPARATOR, i18n.t('validation:fullAge'))
        .required(
          VALIDATION_ERRORS.inputRequired(
            i18n.t('personal-details:dateOfBirth')
          )
        ),
      ...(partnerType === PartnerType.BUSINESS && {
        companyName: Yup.string()
          .matches(
            regExText,
            VALIDATION_ERRORS.valueInvalid(ValidTypeEntered.LETTERS)
          )
          .min(2, i18n.t('validation:minLength', { value: 2 }))
          .max(35, i18n.t('validation:maxLength', { value: 35 }))
          .required(
            VALIDATION_ERRORS.inputRequired(
              i18n.t('personal-details:companyName:VtiGis')
            )
          ),
        dateOfBirth: Yup.string()
          .formatOfDate(
            DATE_FORMAT,
            DATE_SEPARATOR,
            i18n.t('validation:invalidDateformat')
          )
          .notInFuture(DATE_SEPARATOR, i18n.t('validation:dateInFuture'))
          .fullAge(18, DATE_SEPARATOR, i18n.t('validation:fullAge')),
      }),
    }),
  })

export const mobilePhoneNumberSchema = Yup.object().shape({
  mobilePhoneNumber: Yup.string()
    .required(
      VALIDATION_ERRORS.inputRequired(
        i18n.t('personal-details:mobilePhoneNumber')
      )
    )
    .matches(
      regExNumber,
      VALIDATION_ERRORS.valueInvalid(ValidTypeEntered.NUMBERS)
    )
    .min(3, i18n.t('validation:minLength', { value: 3 }))
    .max(15, i18n.t('validation:maxLength', { value: 15 }))
    .validatePhone(VALIDATION_ERRORS.valueInvalid(ValidTypeEntered.NUMBERS)),
})

export const getValidationSchema = (
  isBusiness: boolean | undefined,
  isVtiGis: boolean | undefined,
  partnerType: PartnerType,
  tlsDeps: TlsDeps
) => {
  // eslint-disable-next-line no-nested-ternary
  return isVtiGis
    ? ValidationSchemaVtiGis(partnerType, tlsDeps)
    : isBusiness
      ? ValidationSchemaBusiness
      : ValidationSchemaPrivate(tlsDeps)
}
